import { apiClient } from "@api/client";
import { queryClient } from "@api/queryClient";
import { request } from "@api/request";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { refreshFailedRecordings } from "@transcription/failedRecordingsStore";
import {
  appendChunk,
  clearChunks,
  deletePendingRecording,
  deleteRecordingMeta,
  getPendingRecording,
  getRecordingMeta,
  listPendingRecordings,
  listRecordingMeta,
  readChunks,
  savePendingRecording,
  saveRecordingMeta,
  type PendingRecording,
} from "@transcription/recordingStore";
import { delay } from "@utils/delay";

const SAMPLE_RATE = 16000;
const BYTES_PER_SAMPLE = 2;
const PART_SIZE = 8 * 1024 * 1024;
const MAX_ATTEMPTS = 6;
const BASE_DELAY_MS = 60_000;
const MAX_DELAY_MS = 10 * 60_000;

function encodeWav(chunks: ArrayBuffer[]): Blob {
  const dataLength = chunks.reduce((n, c) => n + c.byteLength, 0);
  const buffer = new ArrayBuffer(44 + dataLength);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++)
      view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataLength, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, SAMPLE_RATE * BYTES_PER_SAMPLE, true);
  view.setUint16(32, BYTES_PER_SAMPLE, true);
  view.setUint16(34, 8 * BYTES_PER_SAMPLE, true);
  writeStr(36, "data");
  view.setUint32(40, dataLength, true);
  let offset = 44;
  for (const chunk of chunks) {
    new Uint8Array(buffer, offset, chunk.byteLength).set(new Uint8Array(chunk));
    offset += chunk.byteLength;
  }
  return new Blob([buffer], { type: "audio/wav" });
}

const STALE_ARTIFACT_STATUSES = new Set([400, 404, 410]);

function isStaleArtifactError(e: unknown): boolean {
  return (
    e instanceof ClinicAuthError &&
    e.status !== undefined &&
    STALE_ARTIFACT_STATUSES.has(e.status)
  );
}

async function ensureArtifact(rec: PendingRecording): Promise<string> {
  if (rec.artifactUuid) return rec.artifactUuid;
  const artifact = await request(() =>
    apiClient.POST("/artifacts", {
      body: {
        kind: "recording",
        content_type: rec.blob.type,
        about: rec.about,
        filename: `recording-${rec.createdAt}.wav`,
        artifact_metadata: null,
      },
    }),
  );
  rec.artifactUuid = artifact.uuid;
  await savePendingRecording(rec);
  return artifact.uuid;
}

async function uploadPending(rec: PendingRecording): Promise<void> {
  const artifactUuid = await ensureArtifact(rec);
  const { blob } = rec;

  let uploaded;
  try {
    uploaded = await request(() =>
      apiClient.GET("/artifacts/{artifact_uuid}/parts", {
        params: { path: { artifact_uuid: artifactUuid } },
      }),
    );
  } catch (e) {
    if (isStaleArtifactError(e)) {
      rec.artifactUuid = undefined;
      await savePendingRecording(rec);
    }
    throw e;
  }
  const done = new Map(uploaded.map((p) => [p.part_number, p.etag]));

  const partCount = Math.max(1, Math.ceil(blob.size / PART_SIZE));
  const parts: { part_number: number; etag: string }[] = [];
  for (let partNumber = 1; partNumber <= partCount; partNumber++) {
    const existing = done.get(partNumber);
    if (existing) {
      parts.push({ part_number: partNumber, etag: existing });
      continue;
    }
    const { url } = await request(() =>
      apiClient.POST("/artifacts/{artifact_uuid}/parts/{part_number}/url", {
        params: {
          path: { artifact_uuid: artifactUuid, part_number: partNumber },
        },
      }),
    );
    const start = (partNumber - 1) * PART_SIZE;
    const res = await fetch(url, {
      method: "PUT",
      body: blob.slice(start, start + PART_SIZE),
    });
    if (!res.ok) throw new Error(`Part ${partNumber} failed: ${res.status}`);
    const etag = res.headers.get("ETag");
    if (!etag)
      throw new Error("Missing ETag — check S3 CORS ExposeHeaders: ETag");
    parts.push({ part_number: partNumber, etag });
  }

  await request(() =>
    apiClient.POST("/artifacts/{artifact_uuid}/complete", {
      params: { path: { artifact_uuid: artifactUuid } },
      body: { parts, transcript_uuid: rec.transcriptUuid ?? null },
    }),
  );

  await queryClient.invalidateQueries({ queryKey: ["artifacts"] });
}

const inFlight = new Set<string>();

async function uploadWithBackoff(rec: PendingRecording): Promise<void> {
  if (inFlight.has(rec.id)) return;
  inFlight.add(rec.id);
  try {
    rec.attempts ??= 0;
    while (rec.attempts < MAX_ATTEMPTS) {
      try {
        await uploadPending(rec);
        await deletePendingRecording(rec.id);
        return;
      } catch {
        rec.attempts += 1;
        if (rec.attempts >= MAX_ATTEMPTS) {
          rec.failed = true;
          await savePendingRecording(rec);
          return;
        }
        await savePendingRecording(rec);
        await delay(
          Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** (rec.attempts - 1)),
        );
      }
    }
  } finally {
    inFlight.delete(rec.id);
    await refreshFailedRecordings();
  }
}

export async function dismissFailedRecording(id: string): Promise<void> {
  await deletePendingRecording(id);
  await clearChunks(id);
  await refreshFailedRecordings();
}

export async function retryFailedRecording(id: string): Promise<void> {
  const rec = await getPendingRecording(id);
  if (!rec) return;
  rec.attempts = 0;
  rec.failed = false;
  await savePendingRecording(rec);
  await refreshFailedRecordings();
  await uploadWithBackoff(rec);
}

async function persistPendingRecording(
  blob: Blob,
  about: string[],
  transcriptUuid?: string,
): Promise<PendingRecording> {
  const rec: PendingRecording = {
    id: crypto.randomUUID(),
    about,
    blob,
    createdAt: Date.now(),
    attempts: 0,
    transcriptUuid,
  };
  await savePendingRecording(rec);
  return rec;
}

const activeRecordingIds = new Set<string>();
const chunkWrites = new Map<string, Promise<unknown>>();

export function enqueueChunk(
  recordingId: string,
  seq: number,
  data: ArrayBuffer,
): void {
  const prev = chunkWrites.get(recordingId) ?? Promise.resolve();
  chunkWrites.set(
    recordingId,
    prev.then(() => appendChunk(recordingId, seq, data)).catch(() => {}),
  );
}

export async function beginRecording(
  recordingId: string,
  about: string[],
): Promise<void> {
  activeRecordingIds.add(recordingId);
  await saveRecordingMeta({ recordingId, about, createdAt: Date.now() });
}

export async function setRecordingTranscript(
  recordingId: string,
  transcriptUuid: string,
): Promise<void> {
  const meta = await getRecordingMeta(recordingId);
  if (!meta) return;
  await saveRecordingMeta({ ...meta, transcriptUuid });
}

export async function finishRecording(
  recordingId: string,
  tail: ArrayBuffer[],
  startSeq: number,
): Promise<void> {
  let pending: PendingRecording | null = null;
  try {
    await chunkWrites.get(recordingId);
    chunkWrites.delete(recordingId);
    let seq = startSeq;
    for (const chunk of tail) await appendChunk(recordingId, seq++, chunk);
    const meta = await getRecordingMeta(recordingId);
    const chunks = await readChunks(recordingId);
    if (chunks.length > 0 && meta && meta.about.length > 0)
      pending = await persistPendingRecording(
        encodeWav(chunks),
        meta.about,
        meta.transcriptUuid,
      );
  } finally {
    await clearChunks(recordingId);
    await deleteRecordingMeta(recordingId);
    activeRecordingIds.delete(recordingId);
  }
  if (pending) await uploadWithBackoff(pending);
}

async function recoverPendingChunks(): Promise<void> {
  for (const meta of await listRecordingMeta()) {
    if (!activeRecordingIds.has(meta.recordingId))
      await finishRecording(meta.recordingId, [], 0);
  }
}

let flushing = false;

export async function flushPendingRecordings(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    await recoverPendingChunks();
    await refreshFailedRecordings();
    for (const rec of await listPendingRecordings()) {
      if (!rec.failed && rec.attempts < MAX_ATTEMPTS)
        await uploadWithBackoff(rec);
    }
  } finally {
    flushing = false;
  }
}
